import React, { useCallback, useEffect, useState } from 'react';
import { Cliente, Proyecto, FaseProyecto, FASE_LABELS, FASE_ORDER } from '../types';
import { useKanban } from '../context/KanbanContext';

interface ProyectosClientePanelProps {
    cliente: Cliente;
    onClose: () => void;
}

export const ProyectosClientePanel: React.FC<ProyectosClientePanelProps> = ({ cliente, onClose }) => {
    const { fetchProyectosCliente, crearProyecto } = useKanban();
    const [proyectos, setProyectos] = useState<Proyecto[]>([]);
    const [loading, setLoading] = useState(true);
    const [mostrarNuevo, setMostrarNuevo] = useState(false);
    const [nombre, setNombre] = useState('');
    const [fase, setFase] = useState<FaseProyecto>('soporte_tecnico');
    const [creando, setCreando] = useState(false);
    const [mensaje, setMensaje] = useState<{ tipo: 'ok' | 'error'; texto: string } | null>(null);

    const cargar = useCallback(async () => {
        setLoading(true);
        const data = await fetchProyectosCliente(cliente.id);
        setProyectos(data);
        setLoading(false);
    }, [cliente.id, fetchProyectosCliente]);

    useEffect(() => { cargar(); }, [cargar]);

    const handleCrear = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!nombre.trim()) return;

        setCreando(true);
        const ok = await crearProyecto(cliente.id, { nombre_proyecto: nombre.trim(), fase });
        setCreando(false);

        if (ok) {
            setNombre('');
            setFase('soporte_tecnico');
            setMostrarNuevo(false);
            setMensaje({ tipo: 'ok', texto: 'Proyecto creado correctamente.' });
            await cargar();
        } else {
            setMensaje({ tipo: 'error', texto: 'No se pudo crear el proyecto.' });
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col transform transition-all"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b border-slate-100">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center text-white text-lg flex-shrink-0">
                            📁
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-lg font-semibold text-slate-800">Proyectos</h2>
                            <p className="text-xs text-slate-500 truncate">{cliente.empresa}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-5 overflow-y-auto flex-1 space-y-3">
                    {mensaje && (
                        <div className={`text-sm rounded-xl px-4 py-2.5 border ${
                            mensaje.tipo === 'ok'
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                : 'bg-red-50 border-red-200 text-red-700'
                        }`}>
                            {mensaje.texto}
                        </div>
                    )}

                    {!mostrarNuevo ? (
                        <button
                            onClick={() => { setMostrarNuevo(true); setMensaje(null); }}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium border-2 border-dashed border-slate-200 text-slate-500 rounded-xl hover:border-blue-300 hover:text-blue-600 transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Nuevo proyecto
                        </button>
                    ) : (
                        <form onSubmit={handleCrear} className="bg-slate-50 rounded-xl p-4 space-y-3">
                            <div>
                                <label className="block text-xs font-medium text-slate-600 mb-1">Nombre del proyecto *</label>
                                <input
                                    type="text"
                                    value={nombre}
                                    onChange={(e) => setNombre(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Ej: Sistema de Inventario"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-600 mb-1">Fase</label>
                                <select
                                    value={fase}
                                    onChange={(e) => setFase(e.target.value as FaseProyecto)}
                                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    {FASE_ORDER.map(f => (
                                        <option key={f} value={f}>{FASE_LABELS[f]}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex justify-end gap-2 pt-1">
                                <button
                                    type="button"
                                    onClick={() => { setMostrarNuevo(false); setNombre(''); }}
                                    className="px-3 py-2 text-xs font-medium border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={creando}
                                    className="px-3 py-2 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                                >
                                    {creando ? 'Creando...' : 'Crear proyecto'}
                                </button>
                            </div>
                        </form>
                    )}

                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-32 gap-3">
                            <div className="relative">
                                <div className="w-10 h-10 border-4 border-slate-200 rounded-full"></div>
                                <div className="absolute inset-0 border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
                            </div>
                            <p className="text-slate-500 text-sm">Cargando proyectos...</p>
                        </div>
                    ) : proyectos.length === 0 ? (
                        <p className="text-center text-sm text-slate-500 py-6">Este cliente todavía no tiene proyectos.</p>
                    ) : (
                        proyectos.map(p => (
                            <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center justify-between gap-3">
                                <p className="text-sm font-medium text-slate-700 truncate">{p.nombre_proyecto}</p>
                                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 flex-shrink-0">
                                    {FASE_LABELS[p.fase]}
                                </span>
                            </div>
                        ))
                    )}
                </div>

                <div className="flex items-center justify-end p-5 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-100 transition-colors font-medium"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};
